import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutSingleComponent, ListSortOption, SelectionAction, SimpleConfigBaseFilter, SkeletonLoaderLayout } from 'gung-list';
import { first, forkJoin, map, Observable } from 'rxjs';
import { CustomerListCardComponent } from '../../components/customer-list-card/customer-list-card.component';
import { Customer } from '../../models';
import { AuthService } from '../auth/auth.service';
import { CustomerSelectionActionConfigService } from '../customer-selection-action-config/customer-selection-action-config.service';
import { CustomerService } from '../customers/customer.service';
import { UsersService } from '../users/users.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerListConfigService implements ConfigService<Customer> {
  public customerService = inject(CustomerService);
  protected usersService = inject(UsersService);
  protected authService = inject(AuthService);
  protected customerSelectionActionConfigService = inject(CustomerSelectionActionConfigService);
  protected translateService = inject(TranslateService);

  skeletonLoaderLayout: SkeletonLoaderLayout = 'list';

  getItems(): Observable<Customer[]> {
    return this.customerService.getCustomers().pipe(first());
  }

  getFilters(): ConfigBaseFilter<Customer>[] {
    return [new CustomerCountryFilter(), new CustomerSalespersonFilter()];
  }

  getSortOptions(): ListSortOption<Customer>[] {
    // TODO: implement
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<Customer>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => CustomerListCardComponent,
        getListLayoutComponent: () => ListLayoutSingleComponent,
        getName: () => 'Customer list'
      }
    ];
  }

  getSearchTerms(item: Customer): string[] {
    return [item.id, item.name];
  }

  getItemId(item: Customer): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }

  getSelectionActions(): Observable<SelectionAction<Customer>[]> {
    return forkJoin([
      this.customerSelectionActionConfigService.getSelectionActions().pipe(first()),
      this.customerSelectionActionConfigService.getDynamicSelectionActions().pipe(first())
    ]).pipe(
      map(([staticActions, dynamicActions]) => {
        return [...staticActions, ...dynamicActions];
      })
    );
  }

  getSelectionActionsButtonTitle(): string {
    return this.translateService.instant('ACTIONS');
  }
}

export class CustomerSalespersonFilter extends SimpleConfigBaseFilter<Customer> {
  getName(): string {
    return 'SALESPERSON';
  }

  getOptionIds(item: Customer): string[] {
    if (!!item.extra?.gme?.salesperson) {
      return [item.extra.gme.salesperson];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class CustomerCountryFilter extends SimpleConfigBaseFilter<Customer> {
  getName(): string {
    return 'COUNTRY';
  }

  getOptionIds(item: Customer): string[] {
    if (!!item.extra?.gme?.address?.countryCode) {
      return [item.extra.gme.address.countryCode];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}